import React, {useLayoutEffect, useRef, useState} from 'react';
import './LobbyFactionGrid.css';
import LobbyFactionArt from './LobbyFactionArt';

const GRID_GAP_PX = 8;
// Есть рамки лиг — они выступают за квадрат плитки (около 10% по бокам и 11%
// сверху и снизу, см. LobbyFactionArt.css): зазор между плитками и поле по
// краям — доли стороны плитки, чтобы рамки не залезали на соседние и за край
export const LOBBY_FRAMED_GAP_RATIO = 0.24;
export const LOBBY_FRAMED_MARGIN_RATIO = 0.12;
// Мельче плитка уже не читается: тогда лучше прокрутка внутри карточки
const GRID_MIN_TILE_PX = 56;
const GRID_MAX_TILE_PX = 150;

/**
 * Сколько столбцов дает самые крупные плитки, при которых ВСЕ count
 * квадратных плиток помещаются в width × height без прокрутки (подпись —
 * поверх арта, места под собой не берет). С рамками лиг (isFramed) между
 * плитками и по краям оставляется место под их выступ.
 *
 * @returns {{columns: number, tileSize: number, gap: number}|null} null — места нет вовсе
 */
export const getFactionGridFit = ({count, width, height, isFramed = false}) => {
  if (!count || width <= 0 || height <= 0) {
    return null;
  }
  const gapRatio = isFramed ? LOBBY_FRAMED_GAP_RATIO : 0;
  const marginRatio = isFramed ? LOBBY_FRAMED_MARGIN_RATIO : 0;
  let best = null;
  for (let columns = 1; columns <= count; columns += 1) {
    const rows = Math.ceil(count / columns);
    const byWidth = (width - (columns - 1) * GRID_GAP_PX) / (columns + (columns - 1) * gapRatio + 2 * marginRatio);
    const byHeight = (height - (rows - 1) * GRID_GAP_PX) / (rows + (rows - 1) * gapRatio + 2 * marginRatio);
    const tileSize = Math.floor(Math.min(byWidth, byHeight, GRID_MAX_TILE_PX));
    if (!best || tileSize > best.tileSize) {
      best = {columns, tileSize, gap: Math.floor(GRID_GAP_PX + tileSize * gapRatio)};
    }
  }
  return best;
};

/**
 * Подбирает столбцы сетки под размер ее опоры: плитки максимально крупные, но
 * все на экране сразу. Без ResizeObserver (тесты, старые браузеры) — null, и
 * сетка раскладывается по CSS (auto-fill, при нехватке места — прокрутка).
 */
const useFactionGridFit = (count, isFramed) => {
  const ref = useRef(null);
  const [fit, setFit] = useState(null);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const measure = () => {
      const style = window.getComputedStyle(node);
      const width = node.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      const height = node.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
      const nextFit = getFactionGridFit({count, width, height, isFramed});
      setFit((prevFit) => (
        prevFit?.columns === nextFit?.columns && prevFit?.tileSize === nextFit?.tileSize
          && prevFit?.gap === nextFit?.gap ? prevFit : nextFit
      ));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [count, isFramed]);

  const isFitted = Boolean(fit) && fit.tileSize >= GRID_MIN_TILE_PX;
  return {ref, fit: isFitted ? fit : null};
};

/**
 * «Все фракции» встроенного выбора десктопного лобби: список карусели без
 * фракций «в разработке» (см. LobbyFactionPicker), плитками с иконками
 * образов (как в «Моей коллекции») — все на экране сразу: столбцов ровно
 * столько, чтобы плитки были максимально крупными и все поместились (не
 * влезли бы и самые мелкие — прокрутка внутри). Выбранная — свечение и
 * галочка. Что делает нажатие (выбор или переход к покупке в карусели),
 * решает вызывающий.
 *
 * @param {Object} props
 * @param {Array<Object>} props.items
 * @param {string|null} props.selectedFactionId - текущая фракция игрока
 * @param {Function} props.getItemDisplay - (item) => getLobbyFactionDisplay
 * @param {Function} props.checkIfItemAvailable - (item) => boolean
 * @param {Function} props.getItemMaskLabel - (item) => подпись закрытой
 * @param {Function} props.onItemClick - (item) => void
 * @param {Object|null} [props.selectFlash] - эффект «выбрано» ({factionId, at})
 */
const LobbyFactionGrid = ({
  items, selectedFactionId, getItemDisplay, checkIfItemAvailable, getItemMaskLabel, onItemClick, selectFlash,
}) => {
  const isFramed = items.some((item) => Boolean(getItemDisplay(item).framePath));
  const {ref, fit} = useFactionGridFit(items.length, isFramed);

  return (
    <div
      ref={ref}
      className={`lobby-faction-grid ${fit ? 'lobby-faction-grid_fitted' : ''}`}
      style={fit ? {
        gridTemplateColumns: `repeat(${fit.columns}, ${fit.tileSize}px)`,
        gap: `${fit.gap}px`,
        '--lobby-faction-grid-tile': `${fit.tileSize}px`,
      } : undefined}
    >
      {items.map((item) => {
        const display = getItemDisplay(item);
        const isAvailable = checkIfItemAvailable(item);
        const isSelected = item.id === selectedFactionId;
        return (
          <button
            type="button"
            key={item.id}
            className={`lobby-faction-grid_tile ${isSelected ? 'lobby-faction-grid_tile_selected' : ''} ${
              isAvailable ? '' : 'lobby-faction-grid_tile_locked'} ${display.framePath ? 'lobby-faction-grid_tile_framed' : ''}`}
            data-faction-id={item.id}
            onClick={() => onItemClick(item)}
            title={isAvailable ? display.name : `${display.name}: ${getItemMaskLabel(item)}`}
          >
            <span className="lobby-faction-grid_art-box">
              {/* Иконки, а не полные арты: плитки мелкие, а лицо на иконке
                  читается лучше крупного арта, ужатого в 100 px */}
              <LobbyFactionArt
                display={display}
                className="lobby-faction-grid_art"
                withFrame
                isIcon
                flashAt={selectFlash?.factionId === item.id ? selectFlash.at : null}
              />
              {!isAvailable && <span className="lobby-faction-grid_mask">{getItemMaskLabel(item)}</span>}
              {isSelected && <img className="lobby-faction-grid_check" src="/img/icons/check.png" alt="Выбрана"/>}
              <span className="lobby-faction-grid_name" style={{color: display.textColor}}>{display.name}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default LobbyFactionGrid;
